import { useState } from "react"
import styled from "styled-components"

import {
  Button,
  cardBoxShadow,
  Checkbox,
  Select,
  spacing,
  Text,
  TextArea,
  TSelectOptions,
} from "@minuthq/meatball-ui-react"

import { TGuestMessage } from "src/data/guests/types/guestTypes"
import { useTranslate } from "src/i18n/useTranslate"
import { ChatPanel } from "src/ui/ChatPanel/ChatPanel"

type TMessageSender = "user" | "guest"

export function GuestChat({
  messages,
  onSendMessage,
  isSending,
  channel,
  onLoadMore,
  isLoadingMore,
  showSendMessageSelector = false,
  showSendAsSelector = false,
}: {
  messages: TGuestMessage[]
  onSendMessage: (args: {
    message: string
    sendAs: TMessageSender
    autoResolve?: boolean
    shouldSendMessage?: boolean
  }) => void
  isSending: boolean
  channel: string
  onLoadMore: () => void
  isLoadingMore: boolean
  showSendAsSelector?: boolean
  showSendMessageSelector?: boolean
}) {
  const { tPlaceholder } = useTranslate()

  const [message, setMessage] = useState("")
  const [autoResolve, setAutoResolve] = useState(false)

  // Configuration state
  const [sendAs, setSendAs] = useState<TMessageSender>("user")
  const [shouldSendMessage, setShouldSendMessage] = useState(false)

  function sendMessage() {
    onSendMessage?.({
      message,
      sendAs,
      autoResolve,
      shouldSendMessage: showSendAsSelector ? shouldSendMessage : undefined,
    })
    setMessage("")
  }

  function handleInputKeyDown(event: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
      event.preventDefault()
      sendMessage()
    }
  }

  function renderInput() {
    return (
      <InputWrapper>
        <ConfigurationWrapper>
          {showSendAsSelector && (
            <Select
              label="Send as"
              options={
                [
                  { label: "User", value: "user" },
                  { label: "Guest", value: "guest" },
                ] satisfies TSelectOptions<TMessageSender>
              }
              value={sendAs}
              onChange={(value) => setSendAs(value as TMessageSender)}
            />
          )}

          {showSendMessageSelector && (
            <Checkbox
              label="Send message"
              checked={shouldSendMessage}
              onCheck={(checked) => setShouldSendMessage(checked)}
            />
          )}
        </ConfigurationWrapper>
        <TextArea
          minRows={4}
          value={message}
          onChange={setMessage}
          onKeyDown={handleInputKeyDown}
        />
        <InputBottomWrapper>
          <Text variant="bodyXS" color="secondary">
            {tPlaceholder("Via")} {channel}
          </Text>
          <SendWrapper>
            <Checkbox
              label="Auto-resolve"
              checked={autoResolve}
              onCheck={setAutoResolve}
            />
            <Button
              color="neutral"
              size="small"
              loading={isSending}
              onClick={sendMessage}
            >
              {tPlaceholder("Send")}
            </Button>
          </SendWrapper>
        </InputBottomWrapper>
      </InputWrapper>
    )
  }

  return (
    <ChatPanel
      messages={messages.map((message) => ({
        id: message.id,
        sentAt: message.timestamp,
        content: message.content,
        source: message.communication_channel,
        sender:
          message.sender_type === "user" || message.sender_type === "minut_ai"
            ? "self"
            : "other",
        badges:
          message.sender_type === "minut_ai"
            ? [
                {
                  color: "promotional",
                  label: "Minut AI",
                  size: "small",
                },
              ]
            : undefined,
      }))}
      onLoadMore={onLoadMore}
      isLoadingMore={isLoadingMore}
      renderInput={renderInput}
    />
  )
}

const InputWrapper = styled.div`
  display: grid;
  gap: ${spacing.XS};
  padding: ${spacing.M};
  margin: 0 ${spacing.M} ${spacing.M} ${spacing.M};
  border-radius: 1rem;
  ${cardBoxShadow};
`

const InputBottomWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const SendWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.XS};
`

const ConfigurationWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: ${spacing.XS};
`
